// The report packet PDF for a signed or released assessment: latest version, render status, older versions.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { post } from '../lib/api.ts';
import { useData } from '../lib/useData.ts';
import { ErrorBox } from './Layout.tsx';

interface Packet { id: string; version: number; pages: string[]; sizeBytes: number | null; sha256: string; createdAt: string; releasedAt: string | null }
interface Job { id: string; status: 'queued' | 'running' | 'done' | 'failed'; reason: string; attempts: number; error: string | null; createdAt: string }

const PAGE_LABEL: Record<string, string> = {
  summary: 'Summary', aerobic: 'Aerobic', body_composition: 'Body composition', coronary_risk: 'Coronary risk', spirometry: 'Spirometry',
};

export function PacketPanel({ assessmentId }: { assessmentId: string }) {
  const { data, error, reload } = useData<{ packets: Packet[]; job: Job | null; replacedBy?: string | null }>(`/api/assessments/${assessmentId}/packets`);
  const [err, setErr] = useState<unknown>(null);
  const [older, setOlder] = useState(false);
  const working = data?.job && (data.job.status === 'queued' || data.job.status === 'running');

  // While the worker has the job, check back every few seconds.
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void reload(), 3000);
    return () => clearInterval(t);
  }, [working, reload]);

  const request = async () => {
    setErr(null);
    try { await post(`/api/assessments/${assessmentId}/packets`); await reload(); } catch (x) { setErr(x); }
  };

  if (!data) return error ? <ErrorBox error={error} /> : null;
  const [latest, ...rest] = data.packets;
  const replaced = !!data.replacedBy;
  return (
    <div className={`card packet no-print${replaced ? ' packet-old' : ''}`}>
      {replaced && (
        <div className="alert error packet-old-banner">
          <b>Old report: this version was replaced by a correction.</b> Do not send or use these PDFs; they open stamped “REPLACED”.{' '}
          <Link to={`/assessments/${data.replacedBy}`}>Open the corrected version and its report →</Link>
        </div>)}
      <div className="spread">
        <div>
          <h2 style={{ margin: 0 }}>{replaced ? 'Old report packet (replaced)' : 'Report packet (PDF)'}</h2>
          {latest ? (
            <div className="muted small">
              Version {latest.version} · {latest.pages.map((p) => PAGE_LABEL[p] ?? p).join(', ')} · made {new Date(latest.createdAt).toLocaleString()}
              {latest.releasedAt ? ' · released' : ''}
            </div>
          ) : !working && <div className="muted small">No PDF has been made for this assessment yet.</div>}
        </div>
        <div className="actions">
          {working && <span className="badge draft">{data.job!.status === 'running' ? 'Making PDF…' : 'PDF queued…'}</span>}
          {latest && <a className={replaced ? 'btn secondary' : 'btn'} href={`/api/packets/${latest.id}/pdf`} target="_blank" rel="noopener">{replaced ? 'Open old PDF' : 'Open PDF'}</a>}
          {latest && !replaced && <a className="btn secondary" href={`/api/packets/${latest.id}/pdf?download=1`}>Download</a>}
          {!working && !replaced && <button className="secondary" onClick={() => void request()}>{latest ? 'Make new version' : 'Make PDF'}</button>}
        </div>
      </div>
      <ErrorBox error={err} />
      {latest && latest.pages.length === 0 && !replaced && (
        <div className="alert error" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          This PDF has no report pages: the results are missing (usually the person's sex or date of birth was missing when it was signed).
          Fix the person's record, then use <b>Create correction</b> to issue a corrected report.
        </div>
      )}
      {data.job?.status === 'failed' && (
        <div className="alert error" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
          The last PDF attempt failed{data.job.error ? `: ${data.job.error}` : ''}. Try again, or use Report → Print.
        </div>
      )}
      {rest.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <button className="link small" onClick={() => setOlder(!older)}>{older ? 'Hide' : 'Show'} {rest.length} earlier version{rest.length > 1 ? 's' : ''}</button>
          {older && <ul className="small">{rest.map((p) => (
            <li key={p.id}><a href={`/api/packets/${p.id}/pdf`} target="_blank" rel="noopener">Version {p.version}</a> · {new Date(p.createdAt).toLocaleString()}
              <span className="muted"> · older version (opens stamped “OLD VERSION”)</span></li>
          ))}</ul>}
        </div>
      )}
    </div>
  );
}
